// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {Toaster} from '@homeaway/react-toaster';
import {Unveil} from '@homeaway/react-unveil';
import {logExperiment} from '@homeaway/abacus-client-utils';
import FooterSeoLinks from '../ConnectedFooterSeoLinks/ConnectedFooterSeoLinks';
import AsyncFooter from '../AsyncFooter/AsyncFooter';
import HeaderBar from '../Header/HeaderBar';
import HtmlTitle from '../HtmlTitle/HtmlTitle';
import DropdownPanelOverlay from '../DropdownPanel/DropdownPanelOverlay';
import OverlayUpdateInterceptor from '../OverlayUpdateInterceptor/OverlayUpdateInterceptor';
import HeadlineWithBreadcrumbs from '../HeadlineWithBreadcrumbs/HeadlineWithBreadcrumbs';
import ButtonBar from '../ButtonBar/ButtonBar';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import SearchBar from '../SearchBar/SearchBar';
import FilterBar from '../FilterBar/FilterBar';
import FilterForm from '../FilterForm/FilterForm';
import {MAP_VIEW} from '../../constants/views';
import Map from '../Map/Map';
import Results from '../Results/Results';
import type {State as ReduxState} from '../../reducers/index';
import withAdsense from '../Ads/AdSense/withAdsense';
import AdSense from '../Ads/AdSense/AdSense';
import {
    MID_AD_BANNER_MOBILE,
    MID_AD_TOWER,
    MID_AD_TOWER_2
} from '../../constants/ads';
import MapHitsDataProvider from '../MapHitsDataProvider/MapHitsDataProvider';
import SearchBannerContainer from '../SearchBannerContainer/SearchBannerContainer';
import SeoContent from '../SeoContent/SeoContent';
import SearchTermCarousel from '../SearchTermCarousel/SearchTermCarousel';
import {media} from '../../utils/breakpoints';
import routerActions from '../../actions/router';
import redirectActions from '../../actions/redirect';
import viewportActions from '../../actions/viewport';
import topBannersActions from '../../actions/topBanners';
import withWindowResize from '../withWindowResize';
import withTopBanners from '../withTopBanners/withTopBanners';
import type {Environment} from '@homeaway/environment-resolver';
import withErrorBoundary from '../ErrorBoundary/withErrorBoundary';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import Feedback from '../ConnectedFeedback/ConnectedFeedback';
import withSearchQuery from '../withSearchQuery/withSearchQuery';
import InternalTools from '../InternalTools/InternalTools';
import {
    HA_SERP_FREE_CANCELLATION,
    HA_SERP_THREE_PACK,
    HA_ADS_HOLDBACK_SERP_AND_PDP,
    HA_ADS_SERP_RIGHTRAIL_300X600,
    HA_TRAVSEG_SERP_FILTER
} from '../../abacus/experiments';

import '../../_overrides.less';
import '../../_utilities.less';
import './Application.less';
import '../../_browser.less';

import type {SearchQueryResponseData} from '../../queries/searchQuery';
import type {CallbackProps as FilteredCountDataShape} from '../FilteredCountDataProvider/FilteredCountDataProvider';
import type {State as AbacusState} from '../../reducers/abacus';
import type {SystemData} from '../../../server/utils/systemData';

type StateProps = {
    abacus: AbacusState,
    adsHoldBack?: Object,
    adsRightRail?: Object,
    allowBanners: boolean,
    environment: Environment,
    freeCancellation?: Object,
    isFullScreenMapView: boolean,
    isInternalUser: boolean,
    isMapShowing: boolean,
    isRedirecting: boolean,
    isThreePackTest: boolean,
    isXsScreenWidth: boolean,
    locale: string,
    shouldShowSegmentation: boolean,
    showConsolidationBanner: boolean,
    showOverlay: boolean,
    siteName: string,
    ssr?: boolean,
    system: SystemData,
    topBannersHeight: number
};

type OwnProps = {
    data: SearchQueryResponseData,
    onBackToList: typeof routerActions.list,
    onResize: typeof viewportActions.sizeChanged,
    onTopBannersSizeChanged: typeof topBannersActions.sizeChanged,
    redirect: typeof redirectActions.redirect
};

type WithAdSenseProps = {
    adSense: {
        key: string,
        shouldShow: boolean,
        updateAdSense: () => void
    }
};

export type Props = StateProps & WithAdSenseProps & OwnProps;

const renderFilterFormWithFilteredCountData = ({
    resultsCount,
    filterGroups
}: FilteredCountDataShape) => (
    <FilterForm resultsCount={resultsCount} filterGroups={filterGroups} />
);

const renderMapWithHitsData = ({hits, ...rest}) => (
    <Map hits={hits} {...rest} />
);

const mapOwnPropsToSeoContentProps = ({data: {results}}: Props) => {
    if (results) {
        const searchReviewsHeadline = results.searchReviewsHeadline;
        const searchReviews = results.searchReviews;
        if (searchReviewsHeadline && searchReviews && searchReviews.length) {
            return {
                searchReviewsHeadline,
                searchReviews
            };
        }
    }
    return null;
};

export class Application extends Component<Props> {
    componentDidMount() {
        const {data, freeCancellation} = this.props;

        const isDateless =
            data &&
            data.results &&
            data.results.hitCollection &&
            data.results.hitCollection.isDatelessSearch;

        if (isDateless === false && freeCancellation) {
            logExperiment(freeCancellation);
        }
    }

    componentDidUpdate() {
        const {data, isFullScreenMapView, onBackToList} = this.props;

        if (isFullScreenMapView && data.error) {
            onBackToList();
        }
    }

    render() {
        const {
            data,
            isFullScreenMapView,
            isInternalUser,
            isMapShowing,
            isThreePackTest,
            adsHoldBack,
            adsRightRail,
            isRedirecting,
            isXsScreenWidth,
            allowBanners,
            onBackToList,
            redirect,
            shouldShowSegmentation,
            showOverlay,
            siteName,
            topBannersHeight
        } = this.props;

        const {error, loading, results} = data;

        const {
            breadcrumbs,
            headlineTitle,
            pidRedirectUrl,
            searchBanner,
            internal,
            canonicalLink,
            pageTitle,
            metaDescription,
            metaKeywords
        } = results || {};

        if (!loading && pidRedirectUrl) {
            redirect(pidRedirectUrl);
        }

        const isRedirectingToList = !!error && isFullScreenMapView;
        const mapButtonDisabled = isXsScreenWidth && !!error;
        const classes = classNames('Application', {
            'Application--overlay': showOverlay,
            'Application--fullScreenMap': isFullScreenMapView,
            'Application--segmentation': shouldShowSegmentation
        });
        const isAdsHoldBack =
            adsHoldBack && (adsHoldBack.value === 1 || adsHoldBack.value === 2);

        const isRightRailV1 = adsRightRail && adsRightRail.value === 1;
        const isRightRailV2 = adsRightRail && adsRightRail.value === 2;
        const isRightRailV3 = adsRightRail && adsRightRail.value === 3;

        const adsRightRailClassesTop = classNames('RightRail__ads', {
            RightRail__variant__1: isRightRailV1,
            RightRail__variant__2: isRightRailV2,
            RightRail__variant__3__1: isRightRailV3,
            HA_SERP_THREE_PACK: isThreePackTest
        });

        const adsRightRailClassesBottom = classNames('RightRail__ads', {
            Remove__RightRail: !isRightRailV3,
            RightRail__variant__3__2: isRightRailV3
        });

        const applicationMapClasses = classNames('Application__map', {
            HA_SERP_THREE_PACK: isThreePackTest,
            'Application__map--fullScreen': isFullScreenMapView,
            Application__map_no_right_rail_ads: isAdsHoldBack,
            Application__map_right_rail_ads_variant:
                isRightRailV1 || isRightRailV2 || isRightRailV3
        });
        const topBannerMargin = {marginTop: topBannersHeight};
        const applicationMapStyles = allowBanners ? topBannerMargin : {};

        const shouldShowHeadlineWithBreadcrumbs = !!(
            (breadcrumbs && breadcrumbs.length) ||
            headlineTitle
        );
        const seoContentProps = mapOwnPropsToSeoContentProps(this.props);
        const isLoadingOrRedirecting =
            loading || isRedirecting || isRedirectingToList;

        const {
            adSense: {
                key: adSenseKey,
                shouldShow: shouldShowAdSense,
                ...restAdSense
            }
        } = this.props;

        const shouldShowPreFooter =
            shouldShowHeadlineWithBreadcrumbs ||
            !!seoContentProps ||
            shouldShowAdSense;

        const showBackToList = isFullScreenMapView;

        return (
            <div className={classes} style={topBannerMargin}>
                <header
                    className="Application__header"
                    data-wdio="HeaderBarContainer"
                >
                    <HtmlTitle
                        pageTitle={pageTitle}
                        metaDescription={metaDescription}
                        metaKeywords={metaKeywords}
                        canonicalLink={canonicalLink}
                    />

                    <HeaderBar
                        internalTools={
                            isInternalUser ? (
                                <InternalTools
                                    searchServiceUrl={
                                        internal && internal.searchServiceUrl
                                    }
                                />
                            ) : null
                        }
                    />
                    <SearchBar />
                </header>
                {isXsScreenWidth && (
                    <section className="Application__buttonBar Application__section--padded">
                        <ButtonBar
                            loading={isLoadingOrRedirecting}
                            mapButtonDisabled={mapButtonDisabled}
                        />
                    </section>
                )}
                <div className="Application__container">
                    {!isXsScreenWidth && (
                        <section
                            className={classNames('Application__filterBar', {
                                HA_SERP_THREE_PACK: isThreePackTest
                            })}
                        >
                            <FilterBar
                                searchResultsLoading={isLoadingOrRedirecting}
                            />
                        </section>
                    )}
                    <div
                        id="Application__resultsViewport"
                        className="Application__resultsViewport"
                    >
                        <OverlayUpdateInterceptor>
                            <div className="Application__resultsColumn">
                                <section className="Application__results Application__section--padded">
                                    <ErrorBoundary>
                                        {!isLoadingOrRedirecting &&
                                            !error && (
                                                <section className="Application__searchTermCarousel">
                                                    <SearchTermCarousel
                                                        results={results}
                                                    />
                                                </section>
                                            )}

                                        {!isLoadingOrRedirecting &&
                                            !error &&
                                            !!searchBanner && (
                                                <section className="Application__searchBanner">
                                                    <SearchBannerContainer
                                                        bannerData={
                                                            searchBanner
                                                        }
                                                    />
                                                </section>
                                            )}

                                        <Results
                                            results={results}
                                            loading={isLoadingOrRedirecting}
                                            error={error}
                                            allowUpdate={!isFullScreenMapView}
                                        />
                                    </ErrorBoundary>
                                </section>
                                {shouldShowPreFooter && (
                                    <section id="Application__preFooter">
                                        <div className="Application__preFooter bg-module-bg-color">
                                            <div
                                                className={classNames(
                                                    'Application__headlineWithBreadcrumbs',
                                                    'Application__section--padded'
                                                )}
                                            >
                                                {shouldShowHeadlineWithBreadcrumbs && (
                                                    <HeadlineWithBreadcrumbs
                                                        breadcrumbs={
                                                            breadcrumbs
                                                        }
                                                        headline={headlineTitle}
                                                    />
                                                )}
                                            </div>
                                            {isXsScreenWidth &&
                                                !isAdsHoldBack && (
                                                    <div
                                                        id={
                                                            MID_AD_BANNER_MOBILE
                                                        }
                                                        className="MobileBanner__ads"
                                                    />
                                                )}
                                            {shouldShowAdSense &&
                                                !isAdsHoldBack && (
                                                    <Unveil
                                                        key={
                                                            adSenseKey /* used so that react will always create a new unveil */
                                                        }
                                                    >
                                                        <div className="Application__section--margined">
                                                            <AdSense
                                                                {...restAdSense}
                                                            />
                                                        </div>
                                                    </Unveil>
                                                )}
                                            {seoContentProps && (
                                                <div
                                                    className={
                                                        'Application__seoContent Application__section--padded'
                                                    }
                                                >
                                                    <SeoContent
                                                        {...seoContentProps}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                                <section
                                    className="Application__footer"
                                    role="contentinfo"
                                    data-wdio="FooterBar"
                                >
                                    {results &&
                                        (results.destination ||
                                            results.seoSerpLinks) && (
                                            <div
                                                className={
                                                    'Application__footerSeoLinks'
                                                }
                                            >
                                                <FooterSeoLinks
                                                    seoSerpLinks={
                                                        results.seoSerpLinks
                                                    }
                                                    destination={
                                                        results.destination
                                                    }
                                                />
                                            </div>
                                        )}
                                    <AsyncFooter />
                                </section>
                            </div>

                            <section
                                className={applicationMapClasses}
                                style={applicationMapStyles}
                            >
                                <MapHitsDataProvider
                                    {...{
                                        error,
                                        loading: isLoadingOrRedirecting,
                                        onBackClick:
                                            showBackToList && onBackToList,
                                        options: {
                                            zoomControl:
                                                isMapShowing &&
                                                !showBackToList /* zoom conflicts with back to list */
                                        },
                                        render: renderMapWithHitsData,
                                        results,
                                        shouldShowFiltersButton: isXsScreenWidth,
                                        showBackToList,
                                        siteName,
                                        allowUpdate: isMapShowing
                                    }}
                                />
                            </section>
                            {!isAdsHoldBack && (
                                <section className="Application__ads">
                                    <div
                                        id={MID_AD_TOWER}
                                        className={adsRightRailClassesTop}
                                    />
                                    <div
                                        id={MID_AD_TOWER_2}
                                        className={adsRightRailClassesBottom}
                                    />
                                </section>
                            )}
                        </OverlayUpdateInterceptor>
                        <DropdownPanelOverlay />
                    </div>
                </div>

                {isXsScreenWidth && (
                    <section className="Application__filterForm">
                        <FilteredCountDataProvider
                            searchResultsLoading={isLoadingOrRedirecting}
                            children={renderFilterFormWithFilteredCountData}
                        />
                    </section>
                )}
                <OverlayUpdateInterceptor>
                    <Toaster />
                    <Feedback />
                </OverlayUpdateInterceptor>
            </div>
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => {
    let {
        searchParams,
        system,
        viewport,
        topBanners: {height: topBannersHeight},
        abacus,
        redirect: {isRedirecting},
        overlay: {showOverlay}
    } = state;

    const breakpoint = viewport.breakpoint;

    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });

    const isSmScreenWidth =
        !media(breakpoint, {
            minWidth: 'MD_MIN'
        }) && !isXsScreenWidth;

    const isMdScreenWidth = media(breakpoint, {
        minWidth: 'MD_MIN'
    });

    const isFullScreenMapView =
        searchParams.params.view === MAP_VIEW &&
        !media(breakpoint, {minWidth: 'MD_MIN'});

    const isMapShowing =
        isFullScreenMapView || media(breakpoint, {minWidth: 'MD_MIN'});

    const allowBanners =
        (isFullScreenMapView && !isSmScreenWidth) ||
        (system.isDesktop && isMdScreenWidth);

    const isInternalUser = system.isInternalUser;
    const freeCancellation = abacus[HA_SERP_FREE_CANCELLATION];
    const adsHoldBack = abacus[HA_ADS_HOLDBACK_SERP_AND_PDP];
    const adsRightRail = abacus[HA_ADS_SERP_RIGHTRAIL_300X600];
    return {
        abacus,
        freeCancellation,
        adsHoldBack,
        adsRightRail,
        environment: system.environment,
        allowBanners,
        isFullScreenMapView,
        isInternalUser,
        isMapShowing,
        isRedirecting,
        isXsScreenWidth,
        locale: system.locale,
        topBannersHeight,
        showOverlay,
        isThreePackTest: !!(abacus[HA_SERP_THREE_PACK] || {}).value,
        shouldShowSegmentation: !!(abacus[HA_TRAVSEG_SERP_FILTER] || {}).value,
        siteName: system.siteName,
        ssr: !!searchParams.params.ssr,
        showConsolidationBanner:
            typeof system.consolidationBanner !== 'undefined' &&
            !system.isMobile &&
            isMdScreenWidth,
        system
    };
};

const mapDispatchToProps = {
    onBackToList: routerActions.list,
    onResize: viewportActions.sizeChanged,
    onTopBannersSizeChanged: topBannersActions.sizeChanged,
    redirect: redirectActions.redirect
};

export default compose(
    withSearchQuery,
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    withWindowResize,
    withErrorBoundary,
    withAdsense,
    withTopBanners
)(Application);
